import { useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent, PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { normalizeSiteLanguage } from '../../utils/currentSiteLanguage';
import {
  CHAT_POSITION_KEY,
  DEFAULT_CHAT_POSITION,
  chatBounds,
  chatPointFromRatio,
  chatPointToRatio,
  clampChatPoint,
  parseChatPosition,
  type ChatBounds,
  type ChatPoint,
} from '../../utils/chatLauncherPosition';
import './draggable-chat-launcher.css';

type Props = {
  visible: boolean;
  unreadCount: number;
  availability: 'online' | 'offline' | null;
  onOpen: () => void;
};

const LABELS = {
  hk: { open: '聯絡客服', hint: '點擊開啟聊天；按住拖動位置。鍵盤方向鍵移動，Home 重設位置。', unread: '未讀訊息', online: '客服在線', offline: '留言聯絡客服' },
  cn: { open: '联系客服', hint: '点击开启聊天；按住拖动位置。键盘方向键移动，Home 重置位置。', unread: '未读消息', online: '客服在线', offline: '留言联系客服' },
  en: { open: 'Chat with us', hint: 'Click to chat. Drag to move. Arrow keys move; Home resets the position.', unread: 'unread messages', online: 'Support online', offline: 'Leave a message' },
  jp: { open: 'チャットで相談', hint: 'クリックでチャット、ドラッグで移動。矢印キーで移動、Homeでリセット。', unread: '未読メッセージ', online: 'オンライン', offline: 'メッセージを残す' },
  kr: { open: '상담 채팅', hint: '클릭하여 채팅하고 드래그하여 이동하세요. 방향키로 이동, Home으로 초기화합니다.', unread: '읽지 않은 메시지', online: '상담원 온라인', offline: '메시지 남기기' },
  th: { open: 'แชทกับเรา', hint: 'แตะเพื่อแชท ลากเพื่อย้าย ใช้ปุ่มลูกศรเพื่อย้ายและ Home เพื่อรีเซ็ต', unread: 'ข้อความที่ยังไม่ได้อ่าน', online: 'เจ้าหน้าที่ออนไลน์', offline: 'ฝากข้อความ' },
  vn: { open: 'Trò chuyện với chúng tôi', hint: 'Nhấn để trò chuyện, kéo để di chuyển. Dùng phím mũi tên để di chuyển, Home để đặt lại.', unread: 'tin nhắn chưa đọc', online: 'Hỗ trợ trực tuyến', offline: 'Để lại lời nhắn' },
  de: { open: 'Mit uns chatten', hint: 'Zum Chatten klicken, zum Verschieben ziehen. Pfeiltasten verschieben, Pos1 setzt zurück.', unread: 'ungelesene Nachrichten', online: 'Support online', offline: 'Nachricht hinterlassen' },
  fr: { open: 'Discuter avec nous', hint: 'Cliquez pour discuter, faites glisser pour déplacer. Flèches pour déplacer, Début pour réinitialiser.', unread: 'messages non lus', online: 'Assistance en ligne', offline: 'Laisser un message' },
  it: { open: 'Chatta con noi', hint: 'Fai clic per chattare, trascina per spostare. Frecce per spostare, Home per ripristinare.', unread: 'messaggi non letti', online: 'Assistenza online', offline: 'Lascia un messaggio' },
  es: { open: 'Chatea con nosotros', hint: 'Haz clic para chatear y arrastra para mover. Flechas para mover, Inicio para restablecer.', unread: 'mensajes sin leer', online: 'Soporte en línea', offline: 'Dejar un mensaje' },
  ug: { open: 'بىز بىلەن پاراڭلىشىڭ', hint: 'پاراڭلىشىش ئۈچۈن بېسىڭ؛ ئورنىنى يۆتكەش ئۈچۈن سۆرەڭ.', unread: 'ئوقۇلمىغان ئۇچۇر', online: 'خېرىدارلار مۇلازىمىتى توردا', offline: 'ئۇچۇر قالدۇرۇڭ' },
};

type DragState = {
  pointerId: number;
  startX: number;
  startY: number;
  origin: ChatPoint;
  moved: boolean;
};

export default function DraggableChatLauncher({ visible, unreadCount, availability, onOpen }: Props) {
  const { i18n } = useTranslation();
  const language = normalizeSiteLanguage(i18n.resolvedLanguage || i18n.language);
  const labels = LABELS[language];
  const hintId = useId();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const positionRef = useRef<ChatPoint>({ x: 0, y: 0 });
  const ratioRef = useRef<ChatPoint>({ ...DEFAULT_CHAT_POSITION });
  const boundsRef = useRef<ChatBounds>({ minX: 0, maxX: 0, minY: 0, maxY: 0 });
  const bucketRef = useRef('');
  const dragRef = useRef<DragState | null>(null);
  const suppressClickRef = useRef(false);
  const [position, setPosition] = useState<ChatPoint | null>(null);
  const [dragging, setDragging] = useState(false);

  const moveTo = (next: ChatPoint) => {
    const point = clampChatPoint(next, boundsRef.current);
    positionRef.current = point;
    setPosition(point);
  };

  const rememberPosition = () => {
    ratioRef.current = chatPointToRatio(positionRef.current, boundsRef.current);
    try {
      window.localStorage.setItem(`${CHAT_POSITION_KEY}_${bucketRef.current}`, JSON.stringify({
        version: 1,
        ...ratioRef.current,
      }));
    } catch {
      // Positioning and chat still work when storage is unavailable.
    }
  };

  useEffect(() => {
    const layout = () => {
      const node = wrapperRef.current;
      if (!node) return;
      const viewport = window.visualViewport;
      const style = window.getComputedStyle(node);
      const safeInset = (side: string) => Number.parseFloat(style.getPropertyValue(`--chat-safe-${side}`)) || 0;
      // Use the layout viewport for the device bucket; opening a keyboard or
      // pinch-zooming should not unexpectedly switch the saved device position.
      const bucket = window.matchMedia('(max-width: 767px)').matches ? 'mobile' : 'desktop';
      if (bucketRef.current !== bucket) {
        bucketRef.current = bucket;
        try {
          ratioRef.current = parseChatPosition(window.localStorage.getItem(`${CHAT_POSITION_KEY}_${bucket}`))
            || { ...DEFAULT_CHAT_POSITION };
        } catch {
          ratioRef.current = { ...DEFAULT_CHAT_POSITION };
        }
      }
      boundsRef.current = chatBounds({
        width: viewport?.width || window.innerWidth,
        height: viewport?.height || window.innerHeight,
        left: viewport?.offsetLeft || 0,
        top: viewport?.offsetTop || 0,
        safeTop: safeInset('top'),
        safeRight: safeInset('right'),
        safeBottom: safeInset('bottom'),
        safeLeft: safeInset('left'),
      }, node.offsetWidth);
      const point = chatPointFromRatio(ratioRef.current, boundsRef.current);
      positionRef.current = point;
      setPosition(point);
      dragRef.current = null;
      setDragging(false);
    };

    layout();
    window.addEventListener('resize', layout);
    window.visualViewport?.addEventListener('resize', layout);
    window.visualViewport?.addEventListener('scroll', layout);
    return () => {
      window.removeEventListener('resize', layout);
      window.visualViewport?.removeEventListener('resize', layout);
      window.visualViewport?.removeEventListener('scroll', layout);
    };
  }, []);

  const onPointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    if (!event.isPrimary || event.button !== 0 || dragRef.current) return;
    suppressClickRef.current = false;
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      origin: positionRef.current,
      moved: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (!drag.moved && Math.hypot(dx, dy) < 8) return;
    drag.moved = true;
    suppressClickRef.current = true;
    setDragging(true);
    event.preventDefault();
    moveTo({ x: drag.origin.x + dx, y: drag.origin.y + dy });
  };

  const finishPointer = (event: PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const touchTap = event.type === 'pointerup' && event.pointerType !== 'mouse' && !drag.moved;
    if (drag.moved) rememberPosition();
    // A cancelled/lost gesture must never start a conversation.
    if (event.type !== 'pointerup') suppressClickRef.current = true;
    dragRef.current = null;
    setDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    // Mobile browsers can suppress the compatibility click immediately after
    // a touch-drag. Open on an unmoved touch/pen release instead, and swallow
    // its later compatibility click so there is still only one maximize call.
    if (touchTap) {
      suppressClickRef.current = true;
      onOpen();
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const step = event.shiftKey ? 32 : 12;
    const delta: Record<string, ChatPoint> = {
      ArrowLeft: { x: -step, y: 0 }, ArrowRight: { x: step, y: 0 },
      ArrowUp: { x: 0, y: -step }, ArrowDown: { x: 0, y: step },
    };
    if (event.key === 'Home') {
      event.preventDefault();
      moveTo(chatPointFromRatio(DEFAULT_CHAT_POSITION, boundsRef.current));
      rememberPosition();
    } else if (delta[event.key]) {
      event.preventDefault();
      moveTo({ x: positionRef.current.x + delta[event.key].x, y: positionRef.current.y + delta[event.key].y });
      rememberPosition();
    }
  };

  return (
    <div
      ref={wrapperRef}
      className="jlbhk-chat-launcher"
      data-dragging={dragging || undefined}
      data-testid="chat-launcher"
      style={{
        visibility: visible && position ? 'visible' : 'hidden',
        transform: `translate3d(${position?.x || 0}px, ${position?.y || 0}px, 0)`,
      }}
      aria-hidden={!visible || !position}
    >
      <button
        ref={buttonRef}
        type="button"
        className="jlbhk-chat-launcher__button"
        aria-label={`${labels.open}${unreadCount ? ` · ${unreadCount} ${labels.unread}` : ''}`}
        aria-describedby={hintId}
        title={`${labels.open} — ${labels.hint}`}
        tabIndex={visible ? 0 : -1}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={finishPointer}
        onPointerCancel={finishPointer}
        onLostPointerCapture={finishPointer}
        onContextMenu={(event) => event.preventDefault()}
        onDragStart={(event) => event.preventDefault()}
        onKeyDown={onKeyDown}
        onClick={(event) => {
          if (suppressClickRef.current && event.detail !== 0) {
            suppressClickRef.current = false;
            event.preventDefault();
            return;
          }
          suppressClickRef.current = false;
          onOpen();
        }}
      >
        <span className="jlbhk-chat-launcher__face" aria-hidden="true">
          <svg viewBox="0 0 32 32" fill="none" className="jlbhk-chat-launcher__icon">
            <path d="M7 17v-2a9 9 0 0 1 18 0v2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            <path d="M8 15H6.5A2.5 2.5 0 0 0 4 17.5v3A2.5 2.5 0 0 0 6.5 23H8V15ZM24 15h1.5a2.5 2.5 0 0 1 2.5 2.5v3a2.5 2.5 0 0 1-2.5 2.5H24v-8Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
            <path d="M25 23a5 5 0 0 1-5 4h-2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            <rect x="14" y="25" width="5" height="3" rx="1.5" fill="currentColor" />
            <path d="M13 16h6M13 20h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
          <span className="jlbhk-chat-launcher__grip"><i /><i /><i /></span>
        </span>
        {unreadCount > 0 ? (
          <span className="jlbhk-chat-launcher__badge" aria-hidden="true" data-testid="chat-unread-count">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        ) : availability && (
          <span
            className="jlbhk-chat-launcher__status"
            data-online={availability === 'online'}
            title={availability === 'online' ? labels.online : labels.offline}
          />
        )}
      </button>
      <span id={hintId} className="sr-only">{labels.hint}</span>
    </div>
  );
}
